import React, { useEffect, useState, useCallback } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import LibraryLayout from "./LibraryLayout";
import {
  librariesList,
  libraryDashboardStats,
  libraryShiftWiseStudents,
  libraryDueSummary,
} from "../../api/Library";
import { Link } from "react-router-dom";
import usePrivateLink from "../config/usePrivateLink";

export default function LibraryDashboard() {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);
  const [libraryId, setLibraryId] = useState("");
  const [libraries, setLibraries] = useState([]);
  const [stats, setStats] = useState(null);
  const [shiftData, setShiftData] = useState([]);
  const [dueRows, setDueRows] = useState([]);
  const [dueSummary, setDueSummary] = useState(null);

  // ── Library filter dropdown ───────────────────────────────
  useEffect(() => {
    librariesList().then((r) => {
      if (r?.data?.status) setLibraries(r.data.data);
    });
  }, []);

  // ── Dashboard stats + shift + due summary ─────────────────
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const qs = libraryId ? `library_id=${libraryId}` : "";

      const [statsRes, shiftRes, dueRes] = await Promise.all([
        libraryDashboardStats(qs),
        libraryShiftWiseStudents(qs),
        libraryDueSummary(qs),
      ]);

      if (statsRes?.data?.status) setStats(statsRes.data.data);
      if (shiftRes?.data?.status) setShiftData(shiftRes.data.data);
      if (dueRes?.data?.status) {
        setDueRows(dueRes.data.data?.slice(0, 6) ?? []);
        setDueSummary(dueRes.data.summary);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  }, [libraryId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");
  const initials = (n) =>
    n
      ?.split(" ")
      .map((x) => x[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() ?? "";

  const totalShiftStudents = shiftData.reduce(
    (a, b) => a + (b.student_count || 0),
    0,
  );

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                <div className="row">
                  <LibraryLayout />

                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                      <div>
                        <h3 className="mb-0 text-primary">Library Dashboard</h3>
                        <p className="m-0">
                          Seats, students, fees and dues at a glance
                        </p>
                      </div>
                      <select
                        className="form-select"
                        style={{ width: "220px" }}
                        value={libraryId}
                        onChange={(e) => setLibraryId(e.target.value)}
                      >
                        <option value="">All Libraries</option>
                        {libraries.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* ── KPI cards ─────────────────────────── */}
                    <div className="row mb-1">
                      <div className="col-sm-6 col-lg-3 mb-3">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body d-flex align-items-center">
                            <div
                              className="avatar bg-label-primary rounded me-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: "60px",
                                height: "60px",
                                minWidth: "60px",
                              }}
                            >
                              <i className="ri-layout-grid-line fs-3"></i>
                            </div>
                            <div>
                              <small className="text-muted">Total Seats</small>
                              <h4 className="mb-0">
                                {stats?.seats?.total ?? 0}
                              </h4>
                              <small className="text-muted">
                                {stats?.seats?.occupancy_rate ?? 0}% occupied
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-sm-6 col-lg-3 mb-3">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body d-flex align-items-center">
                            <div
                              className="avatar bg-label-success rounded me-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: "60px",
                                height: "60px",
                                minWidth: "60px",
                              }}
                            >
                              <i className="ri-graduation-cap-line fs-3"></i>
                            </div>
                            <div>
                              <small className="text-muted">
                                Active Students
                              </small>
                              <h4 className="mb-0">
                                {stats?.students?.active ?? 0}
                              </h4>
                              <small className="text-muted">
                                {stats?.students?.inactive ?? 0} inactive
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-sm-6 col-lg-3 mb-3">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body d-flex align-items-center">
                            <div
                              className="avatar bg-label-info rounded me-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: "60px",
                                height: "60px",
                                minWidth: "60px",
                              }}
                            >
                              <i className="ri-money-rupee-circle-line fs-3"></i>
                            </div>
                            <div>
                              <small className="text-muted">
                                Monthly Revenue
                              </small>
                              <h4 className="mb-0">
                                {inr(stats?.revenue?.current_month)}
                              </h4>
                              <small
                                className={
                                  stats?.revenue?.change_pct >= 0
                                    ? "text-success"
                                    : "text-danger"
                                }
                              >
                                {stats?.revenue?.change_pct >= 0 ? "+" : ""}
                                {stats?.revenue?.change_pct ?? 0}% vs last month
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-sm-6 col-lg-3 mb-3">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body d-flex align-items-center">
                            <div
                              className="avatar bg-label-danger rounded me-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: "60px",
                                height: "60px",
                                minWidth: "60px",
                              }}
                            >
                              <i className="ri-error-warning-line fs-3"></i>
                            </div>
                            <div>
                              <small className="text-muted">
                                Total Outstanding
                              </small>
                              <h4 className="mb-0">
                                {inr(stats?.dues?.total_outstanding)}
                              </h4>
                              <small className="text-danger">
                                {stats?.dues?.overdue_students ?? 0} students
                                overdue
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Seat occupancy + Shift distribution ── */}
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h5 className="mb-0">Seat Occupancy</h5>
                              <Link
                                to="/library/seats"
                                className="btn btn-sm btn-label-primary"
                              >
                                View Seat Map
                              </Link>
                            </div>

                            {stats?.seats && (
                              <>
                                <div
                                  className="progress mb-2"
                                  style={{ height: "10px" }}
                                >
                                  <div
                                    className="progress-bar bg-danger"
                                    style={{
                                      width: `${stats.seats.total > 0
                                          ? (stats.seats.occupied /
                                            stats.seats.total) *
                                          100
                                          : 0
                                        }%`,
                                    }}
                                  />
                                  <div
                                    className="progress-bar bg-success"
                                    style={{
                                      width: `${stats.seats.total > 0
                                          ? (stats.seats.vacant /
                                            stats.seats.total) *
                                          100
                                          : 0
                                        }%`,
                                    }}
                                  />
                                  <div
                                    className="progress-bar bg-secondary"
                                    style={{
                                      width: `${stats.seats.total > 0
                                          ? (stats.seats.oor /
                                            stats.seats.total) *
                                          100
                                          : 0
                                        }%`,
                                    }}
                                  />
                                </div>

                                <div className="d-flex flex-wrap gap-3 mb-3">
                                  <small className="text-muted">
                                    <i className="ri-checkbox-blank-circle-fill text-danger me-1"></i>
                                    Occupied {stats.seats.occupied}
                                  </small>
                                  <small className="text-muted">
                                    <i className="ri-checkbox-blank-circle-fill text-success me-1"></i>
                                    Vacant {stats.seats.vacant}
                                  </small>
                                  <small className="text-muted">
                                    <i className="ri-checkbox-blank-circle-fill text-secondary me-1"></i>
                                    Out of order {stats.seats.oor}
                                  </small>
                                </div>

                                <h2 className="mb-0 text-primary">
                                  {stats.seats.occupancy_rate}%
                                </h2>
                                <small className="text-muted">
                                  Occupancy rate
                                </small>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body">
                            <h5 className="mb-3">Shift-wise Students</h5>
                            {shiftData.length === 0 ? (
                              <p className="text-muted mb-0">
                                No shift data available
                              </p>
                            ) : (
                              shiftData.map((s, i) => (
                                <div
                                  key={i}
                                  className="d-flex align-items-center gap-2 mb-3"
                                >
                                  <small
                                    className="text-muted text-truncate"
                                    style={{ minWidth: "90px" }}
                                  >
                                    {s.shift_name}
                                  </small>
                                  <div
                                    className="progress flex-grow-1"
                                    style={{ height: "8px" }}
                                  >
                                    <div
                                      className="progress-bar bg-primary"
                                      style={{
                                        width: `${totalShiftStudents > 0
                                            ? Math.round(
                                              (s.student_count /
                                                totalShiftStudents) *
                                              100,
                                            )
                                            : 0
                                          }%`,
                                      }}
                                    />
                                  </div>
                                  <small
                                    className="fw-semibold"
                                    style={{ minWidth: "24px" }}
                                  >
                                    {s.student_count}
                                  </small>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Due payments tracker ─────────────── */}
                    <div className="col-md-12">
                      <div className="card border-0 shadow-sm mb-3">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <div>
                              <h4 className="text-primary mb-0">
                                Due Payment Tracker
                              </h4>
                              <small className="text-muted">
                                Students with pending or overdue fee payments
                              </small>
                            </div>
                            {hasAccess("/library/fees") && (
                              <Link
                                to="/library/fees"
                                className="btn btn-sm btn-primary"
                              >
                                <i className="ri-receipt-line me-1"></i>
                                Collect Fees
                              </Link>
                            )}
                          </div>

                          {dueSummary && (
                            <div className="row mb-3">
                              <div className="col-sm-4 mb-2">
                                <div className="p-3 rounded bg-label-danger">
                                  <small>Students with dues</small>
                                  <h4 className="mb-0">
                                    {dueSummary.total_students_due}
                                  </h4>
                                </div>
                              </div>
                              <div className="col-sm-4 mb-2">
                                <div className="p-3 rounded bg-label-warning">
                                  <small>Total outstanding</small>
                                  <h4 className="mb-0">
                                    {inr(dueSummary.total_outstanding)}
                                  </h4>
                                </div>
                              </div>
                              <div className="col-sm-4 mb-2">
                                <div className="p-3 rounded bg-label-danger">
                                  <small>Overdue month slots</small>
                                  <h4 className="mb-0">
                                    {dueSummary.total_overdue_months}
                                  </h4>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="table-responsive">
                            <table className="table table-hover align-middle">
                              <thead className="table-light">
                                <tr>
                                  <th>Student</th>
                                  <th>Seat / Shift</th>
                                  <th>Months Due</th>
                                  <th>Outstanding</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {dueRows.length > 0 ? (
                                  dueRows.map((row) => (
                                    <tr key={row.library_student_id}>
                                      <td>
                                        <div className="d-flex align-items-center">
                                          {/* <div
                                            className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center me-2"
                                            style={{
                                              width: "36px",
                                              height: "36px",
                                              minWidth: "36px",
                                              fontSize: "12px",
                                            }}
                                          >
                                            {initials(row.student_name)}
                                          </div> */}
                                          <div>
                                            <div className="fw-semibold">
                                              {row.student_name}
                                            </div>
                                            <small className="text-muted">
                                              {row.mobile}
                                            </small>
                                          </div>
                                        </div>
                                      </td>
                                      <td>
                                        {row.seat_no
                                          ? `Seat ${row.seat_no}`
                                          : "—"}
                                        {row.shift_name && (
                                          <>
                                            <br />
                                            <small className="text-muted">
                                              {row.shift_name}
                                            </small>
                                          </>
                                        )}
                                      </td>
                                      <td>
                                        <span className="badge bg-label-warning me-1">
                                          {row.months_due} pending
                                        </span>
                                        {row.overdue_months > 0 && (
                                          <span className="badge bg-label-danger">
                                            {row.overdue_months} overdue
                                          </span>
                                        )}
                                      </td>
                                      <td className="fw-semibold text-danger">
                                        {inr(row.total_due)}
                                      </td>
                                      <td>
                                        <Link
                                          to={`/library/fees?student=${row.library_student_id}`}
                                          className="btn btn-sm btn-label-primary"
                                        >
                                          Collect
                                        </Link>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="5">
                                      <div className="text-center py-4">
                                        <i className="ri-emotion-happy-line fs-1 text-muted"></i>
                                        <h6 className="mt-2">
                                          No outstanding dues
                                        </h6>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          {dueRows.length > 0 && (
                            <div className="text-center mt-2">
                              <Link
                                to="/library/dues"
                                className="btn btn-sm btn-label-primary"
                              >
                                View all dues →
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
